import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { create, findUnregisteredSeries } from "./api-bimestre";
import session from "../../SessionHandler/session-helper";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Container from "../UI/Container";
import Loading from "../UI/Loading";
import TextFieldDate from "../UI/TextFieldDate";

const MAX_BIMESTRES = 4;
const DATAS_VAZIAS = Array.from({ length: MAX_BIMESTRES }, () => ({ dataInicio: null, dataFim: null }));

const isValida = (data) => data && dayjs(data).isValid();

/** Erros por bimestre: { dataInicio?, dataFim? } para os `quant` primeiros. */
const validar = (datas, quant) =>
  datas.slice(0, quant).map(({ dataInicio, dataFim }, i) => {
    const erros = {};
    if (isValida(dataInicio) && isValida(dataFim) && dayjs(dataFim).isBefore(dayjs(dataInicio), "day")) {
      erros.dataFim = "Não pode ser anterior à data início.";
    }
    const fimAnterior = i > 0 ? datas[i - 1].dataFim : null;
    if (isValida(dataInicio) && isValida(fimAnterior) && !dayjs(dataInicio).isAfter(dayjs(fimAnterior), "day")) {
      erros.dataInicio = "Deve ser depois do fim do bimestre anterior.";
    }
    return erros;
  });

export default function BimestreForm() {
  const navigate = useNavigate();
  const anoLetivo = session.getAnoLetivo();
  const [series, setSeries] = useState([]);
  const [isLoadingSeries, setIsLoadingSeries] = useState(true);
  const [serie, setSerie] = useState(null);
  const [quantBimestre, setQuantBimestre] = useState(MAX_BIMESTRES);
  const [datas, setDatas] = useState(DATAS_VAZIAS);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    findUnregisteredSeries()
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
        } else if (Array.isArray(data)) {
          setSeries(data);
        }
      })
      .finally(() => setIsLoadingSeries(false));
  }, []);

  const erros = useMemo(() => validar(datas, quantBimestre), [datas, quantBimestre]);
  const ativos = datas.slice(0, quantBimestre);
  const completo = ativos.every((d) => isValida(d.dataInicio) && isValida(d.dataFim));
  const temErro = erros.some((e) => e.dataInicio || e.dataFim);
  const podeSalvar = Boolean(serie) && completo && !temErro && !isSaving;

  const handleDataChange = (index, campo, value) => {
    setDatas((prevState) =>
      prevState.map((d, i) => (i === index ? { ...d, [campo]: value ? dayjs(value) : null } : d)),
    );
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!podeSalvar) return;
    // A série é aplicada a todos os bimestres no envio (antes ficava presa à escolhida
    // quando cada data era digitada).
    const bimestres = ativos.map((d, i) => ({
      serie,
      anoLetivo,
      dataInicio: d.dataInicio,
      dataFim: d.dataFim,
      numeroBimestre: i + 1,
    }));
    setIsSaving(true);
    create(bimestres)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível cadastrar os bimestres.");
          return;
        }
        success(`Bimestres da série ${serie.descricao} cadastrados.`);
        navigate("/bimestre");
      })
      .finally(() => setIsSaving(false));
  };

  const renderConteudo = () => {
    if (isLoadingSeries) {
      return (
        <Box py={6}>
          <Loading description="Carregando séries..." />
        </Box>
      );
    }
    if (series.length === 0) {
      return (
        <Alert severity="success">
          Todas as séries já têm bimestres cadastrados no ano letivo {anoLetivo}.
        </Alert>
      );
    }
    return (
      <Stack component="form" onSubmit={clickSubmit} spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Autocomplete
            options={series}
            value={serie}
            onChange={(event, value) => setSerie(value)}
            getOptionLabel={(option) => option.descricao ?? ""}
            isOptionEqualToValue={(option, value) => option.codSerie === value.codSerie}
            noOptionsText="Nenhuma série encontrada"
            renderInput={(params) => <TextField {...params} size="small" label="Série" required />}
            sx={{ minWidth: 280, flexGrow: 1, maxWidth: { md: 420 } }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Quantidade:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={quantBimestre}
              onChange={(event, value) => value && setQuantBimestre(value)}
              aria-label="Quantidade de bimestres"
            >
              {[1, 2, 3, 4].map((q) => (
                <ToggleButton key={q} value={q}>
                  {q}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        <Stack spacing={1.5}>
          {ativos.map((d, i) => (
            <Paper key={i} variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 3 }} display="flex" alignItems="center" minHeight={40}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {i + 1}º Bimestre
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4.5 }}>
                  <TextFieldDate
                    label="Data Início"
                    value={d.dataInicio}
                    onChange={(value) => handleDataChange(i, "dataInicio", value)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        required: true,
                        error: Boolean(erros[i]?.dataInicio),
                        helperText: erros[i]?.dataInicio || " ",
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4.5 }}>
                  <TextFieldDate
                    label="Data Fim"
                    value={d.dataFim}
                    onChange={(value) => handleDataChange(i, "dataFim", value)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        required: true,
                        error: Boolean(erros[i]?.dataFim),
                        helperText: erros[i]?.dataFim || " ",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>

        <Box>
          <Button
            type="submit"
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
            disabled={!podeSalvar}
          >
            Cadastrar Bimestres
          </Button>
          {!podeSalvar && !isSaving && (
            <Typography variant="caption" color="text.secondary" component="div" mt={1}>
              {!serie
                ? "Selecione a série."
                : !completo
                  ? "Preencha as datas de início e fim de todos os bimestres."
                  : "Corrija as datas destacadas."}
            </Typography>
          )}
        </Box>
      </Stack>
    );
  };

  return (
    <Container>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Tooltip title="Voltar para o calendário">
          <IconButton edge="start" onClick={() => navigate("/bimestre")}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h6">Cadastrar Bimestres da Série</Typography>
          <Typography variant="body2" color="text.secondary">
            Defina o período de cada bimestre no ano letivo {anoLetivo}.
          </Typography>
        </Box>
      </Stack>
      {renderConteudo()}
    </Container>
  );
}
